import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Y from "yjs";
import { HocuspocusProvider } from "@hocuspocus/provider";
import { API_URL } from "@/zapi-frontend/shared/api/axios";
import { randomColor } from "@/zapi-frontend/utils/colors";
import { useAuthenticated } from "@/zapi-frontend/shared/hooks/useAuthenticated";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { reportErrorToSentry } from "@/zapi-frontend/utils/report-error-to-sentry";

const webSocketApiUrl = API_URL.replace("http", "ws");

// From https://www.iana.org/assignments/websocket/websocket.xhtml
const WS_CLOSE_CODES: Record<number, string> = {
  1000: "Normal closure",
  1001: "Going away",
  1002: "Protocol error",
  1003: "Unsupported data",
  1004: "Reserved",
  1005: "No status received (reserved)",
  1006: "Abnormal closure (reserved)",
  1007: "Invalid payload",
  1008: "Policy violation",
  1009: "Message too big",
  1010: "Mandatory extension",
  1011: "Internal error",
  1012: "Service restart",
  1013: "Try again later",
  1014: "Bad gateway",
  1015: "TLS handshake failure (reserved)"
};

export function useYdocProvider(projectId: string) {
  const ydocRef = useRef<Y.Doc | null>(null);
  const providerRef = useRef<HocuspocusProvider | null>(null);

  const [fullySynced, setFullySynced] = useState(false);

  const { activeChapter } = useChapterStore();

  const chapterId = useMemo(() => activeChapter?.id?.toString() || "", [activeChapter]);

  const { me } = useAuthenticated();

  const userName = useMemo(() => {
    return me?.firstName + " " + me?.lastName;
  }, [me]);

  const iAmLeader = useCallback(() => {
    if (!fullySynced) return false;
    const provider = providerRef.current;
    if (!provider || !provider.awareness) return false;
    const myId = provider.awareness.clientID;
    const allStates = provider.awareness.getStates();
    const oldestUserId = Array.from(allStates.entries()).reduce((oldestId, [id, state]) => {
      const currentConnectedSince = state.user?.connectedSince;
      const oldestConnectedSince = allStates.get(oldestId)?.user?.connectedSince;

      if (!oldestConnectedSince || currentConnectedSince < oldestConnectedSince) {
        return id;
      }
      return oldestId;
    }, myId);
    return myId === oldestUserId;
  }, [providerRef, fullySynced]);

  useEffect(() => {
    // WARN do not remove
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line no-console
    window["getConnectedlUsers"] = () => console.log("Connected users: ", providerRef.current?.awareness?.getStates());
  }, []);

  const loggedOnce = useRef(false);
  const logErr = useCallback(
    (message: string, code: string, extra: Record<string, unknown> = {}) => {
      reportErrorToSentry(message, "Yjs/Hocuspocus", code, {
        projectId,
        chapterId,
        userId: me?.id,
        ...extra
      });
    },
    [projectId, chapterId, me?.id]
  );

  useEffect(() => {
    if (!chapterId || !projectId) {
      return;
    }

    loggedOnce.current = false;
    const wsUrl = `${webSocketApiUrl}/projects/${projectId}/chapter/${chapterId}/memo-collaboration`;
    const ydoc = new Y.Doc();
    const provider = new HocuspocusProvider({
      url: wsUrl,
      name: wsUrl,
      document: ydoc,
      onSynced: (data) => {
        if (data.state) setFullySynced(true);
      },
      onClose: ({ event }) => {
        const clean = event.code === 1000 || event.code === 1001;
        if (!clean) {
          if (loggedOnce.current) return;
          loggedOnce.current = true;
          logErr("Yjs Provider WebSocket ERROR", "ydoc_ws_close", {
            code: event.code,
            reason: event.reason,
            description: WS_CLOSE_CODES[event.code]
          });
        }
      }
    });
    providerRef.current = provider;
    ydocRef.current = ydoc;

    providerRef.current?.awareness?.setLocalStateField("user", {
      name: userName,
      color: randomColor(),
      connectedSince: Date.now()
    });

    return () => {
      if (providerRef.current) {
        providerRef.current.disconnect();
        providerRef.current.destroy();
      }
      ydocRef.current = null;
      providerRef.current = null;
      setFullySynced(false);
    };
  }, [chapterId, projectId, userName, logErr]);

  return {
    provider: providerRef.current,
    ydoc: ydocRef.current,
    userName,
    iAmLeader,
    loading: !providerRef.current || !fullySynced
  };
}
